import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Phone, ArrowRight, CalendarCheck } from "lucide-react";
import GoogleRatingBadge from "./GoogleRatingBadge";
import { publicApi } from "../lib/api";
import { consentChoice } from "./CookieConsent";

const KEY = "cass_discovery_seen";

export default function DiscoveryPopup() {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    // The exit-intent popup is opt-in — controlled from Admin → Homepage Promo Popup.
    publicApi.promo().then((p) => setEnabled(!!p?.exit_intent_enabled)).catch(() => setEnabled(false));
  }, []);

  useEffect(() => {
    if (!enabled) return;
    if (sessionStorage.getItem(KEY)) return;

    // Grace period: don't let anything fire until the visitor has been on site a while,
    // so the exit-intent popup never interrupts someone who's just arrived / is scrolling.
    const ARM_DELAY = 25000;   // exit-intent only armed after 25s
    const FALLBACK = 60000;    // otherwise show after 60s
    let armed = false;

    const trigger = () => {
      if (sessionStorage.getItem(KEY)) return;
      // Never cover an unanswered cookie banner: this overlay sits above it and
      // would swallow the Accept and Reject clicks.
      if (!consentChoice()) return;
      sessionStorage.setItem(KEY, "1");
      setOpen(true);
      cleanup();
    };

    // Desktop: exit-intent — cursor leaves through the top of the viewport (only once armed)
    const onMouseOut = (e) => {
      if (!armed) return;
      if (!e.relatedTarget && e.clientY <= 0) trigger();
    };

    const armTimer = setTimeout(() => { armed = true; }, ARM_DELAY);
    const timer = setTimeout(trigger, FALLBACK);

    const cleanup = () => {
      document.removeEventListener("mouseout", onMouseOut);
      clearTimeout(timer);
      clearTimeout(armTimer);
    };

    document.addEventListener("mouseout", onMouseOut);
    return cleanup;
  }, [enabled]);

  const close = () => setOpen(false);

  if (!open) return null;

  return (
    <div
      data-testid="discovery-popup-overlay"
      className="fixed inset-0 z-[120] flex items-center justify-center bg-navy-deep/80 backdrop-blur-sm p-4 animate-in fade-in-0 duration-200"
      onClick={close}
    >
      <div
        className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="discovery-popup-close"
          onClick={close}
          aria-label="Close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-navy transition-colors z-10"
        >
          <X size={18} />
        </button>

        <div className="numbers-pattern text-white px-8 pt-10 pb-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-orange flex items-center justify-center mx-auto mb-4 shadow-lg">
            <CalendarCheck size={26} className="text-white" />
          </div>
          <h2 className="font-display font-extrabold text-2xl md:text-3xl leading-tight">Let's Talk About Your Business</h2>
          <p className="text-slate-300 text-sm mt-2 leading-relaxed">Want the price first? It's published — build your package in about two minutes, no email needed. Or tell us about your business and we'll recommend what's genuinely right for you. No pressure either way.</p>
        </div>

        <div className="p-8 pt-6 space-y-3">
          <Link
            to="/pricing"
            data-testid="discovery-popup-pricing"
            onClick={close}
            className="w-full inline-flex items-center justify-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-6 py-3.5 rounded-full transition-all hover:-translate-y-0.5"
          >
            See your price <ArrowRight size={18} />
          </Link>
          <Link
            to="/contact"
            data-testid="discovery-popup-book"
            onClick={close}
            className="w-full inline-flex items-center justify-center gap-2 border-2 border-navy/15 hover:border-navy text-navy font-bold px-6 py-3 rounded-full transition-all"
          >
            Let's Talk <ArrowRight size={18} />
          </Link>
          <a
            href="tel:01827780550"
            className="w-full inline-flex items-center justify-center gap-2 text-slate-500 hover:text-orange font-semibold text-sm pt-1 transition-colors"
          >
            <Phone size={15} /> Or call us on 01827 780550
          </a>

          <div className="pt-4 mt-2 border-t border-slate-100 flex flex-col items-center justify-center gap-1.5 text-center">
            <GoogleRatingBadge />
            <span className="text-xs text-slate-400 font-medium">Xero Platinum Partner · a decade of cloud accounting expertise</span>
          </div>
        </div>
      </div>
    </div>
  );
}
